import { useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Building2,
  Radio,
} from 'lucide-react';
import {
  usePropertyContentFull,
  useAmenityCatalogue,
  usePlatformPropertyDetail,
} from '@/hooks/api/usePlatform';
import { EmptyState } from '@/components/ui/EmptyState';
import { SetupPmsAccessButton } from '@/components/admin/SetupPmsAccessButton';
import { cn } from '@/lib/utils';
import { OverviewTab } from '@/components/admin/property-editor/OverviewTab';
import { GuidelinesTab } from '@/components/admin/property-editor/GuidelinesTab';
import { RoomsTab } from '@/components/admin/property-editor/RoomsTab';
import { AmenitiesTab } from '@/components/admin/property-editor/AmenitiesTab';
import { PhotosTab } from '@/components/admin/property-editor/PhotosTab';
import { ContactsKycTab } from '@/components/admin/property-editor/ContactsKycTab';
import { PublishTab } from '@/components/admin/property-editor/PublishTab';
import { PlatformEditorApiProvider } from '@/components/admin/property-editor/api-context';

type TabKey = 'overview' | 'guidelines' | 'rooms' | 'amenities' | 'photos' | 'contacts' | 'publish';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'guidelines', label: 'Guidelines & FAQ' },
  { key: 'rooms', label: 'Rooms & rates' },
  { key: 'amenities', label: 'Amenities' },
  { key: 'photos', label: 'Photos & video' },
  { key: 'contacts', label: 'Contacts & KYC' },
  { key: 'publish', label: 'Publish' },
];

const TAB_KEYS = TABS.map((t) => t.key);

export function AdminPropertyEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const rawTab = searchParams.get('tab');
  const activeTab: TabKey = TAB_KEYS.includes(rawTab as TabKey) ? (rawTab as TabKey) : 'overview';

  const { data, isLoading, isError, error, refetch } = usePropertyContentFull(id ?? null);
  const { data: catalogue } = useAmenityCatalogue();
  // Light detail read solely to learn this property's operator (tenant) name,
  // which the "Set up PMS access" action resolves to a tenantId.
  const { data: propertyDetail } = usePlatformPropertyDetail(id ?? null);

  const setTab = useCallback(
    (key: TabKey) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      });
    },
    [setSearchParams],
  );

  const onTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    else return;
    e.preventDefault();
    setTab(TABS[next].key);
    tabRefs.current[next]?.focus();
  };

  const isNotFound =
    isError && (error as { response?: { status?: number } })?.response?.status === 404;

  return (
    <PlatformEditorApiProvider>
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate('/admin/properties')}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
        >
          <ArrowLeft size={15} aria-hidden="true" />
          Properties
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
          <span className="ml-2 text-sm text-gray-500">Loading property…</span>
        </div>
      ) : isNotFound ? (
        <div className="rounded-xl border border-gray-200 bg-white">
          <EmptyState
            icon={Building2}
            title="Property not found"
            description="This listing may have been removed."
            actionLabel="Back to properties"
            onAction={() => navigate('/admin/properties')}
          />
        </div>
      ) : isError || !data ? (
        <div className="rounded-xl border border-gray-200 bg-white py-16 text-center">
          <AlertCircle size={24} className="mx-auto mb-3 text-red-500" aria-hidden="true" />
          <p className="mb-4 text-sm text-red-700">Unable to load this property.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{data.property.name}</h1>
              <p className="mt-1 text-sm text-gray-500">
                {data.property.city}
                {data.property.country ? `, ${data.property.country}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold',
                  data.publishGate.isLive ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800',
                )}
              >
                <Radio size={14} aria-hidden="true" />
                {data.publishGate.isLive ? 'Live' : 'Draft'}
              </span>
              <SetupPmsAccessButton
                propertyName={data.property.name}
                operatorName={propertyDetail?.operatorName}
              />
            </div>
          </div>

          <div className="border-b border-gray-200">
            <div role="tablist" aria-label="Property editor sections" className="-mb-px flex flex-wrap gap-1 overflow-x-auto">
              {TABS.map((tab, index) => {
                const selected = tab.key === activeTab;
                return (
                  <button
                    key={tab.key}
                    ref={(el) => {
                      tabRefs.current[index] = el;
                    }}
                    role="tab"
                    id={`tab-${tab.key}`}
                    aria-selected={selected}
                    aria-controls={`panel-${tab.key}`}
                    tabIndex={selected ? 0 : -1}
                    onClick={() => setTab(tab.key)}
                    onKeyDown={(e) => onTabKeyDown(e, index)}
                    className={cn(
                      'whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-t',
                      selected
                        ? 'border-brand-600 text-brand-700'
                        : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300',
                    )}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`} tabIndex={0} className="focus-visible:outline-none">
            {activeTab === 'overview' && <OverviewTab property={data.property} />}
            {activeTab === 'guidelines' && (
              <GuidelinesTab propertyId={data.property.id} profile={data.profile} />
            )}
            {activeTab === 'rooms' && (
              <RoomsTab
                propertyId={data.property.id}
                units={data.units}
                currency={data.property.currency}
                publishGate={data.publishGate}
              />
            )}
            {activeTab === 'amenities' && (
              <AmenitiesTab
                propertyId={data.property.id}
                catalogue={catalogue ?? data.amenities.catalogue}
                selected={data.amenities.selected}
              />
            )}
            {activeTab === 'photos' && (
              <PhotosTab propertyId={data.property.id} media={data.media} />
            )}
            {activeTab === 'contacts' && (
              <ContactsKycTab propertyId={data.property.id} contacts={data.contacts} kyc={data.kyc} />
            )}
            {activeTab === 'publish' && (
              <PublishTab property={data.property} publishGate={data.publishGate} pms={data.pms} />
            )}
          </div>
        </>
      )}
    </div>
    </PlatformEditorApiProvider>
  );
}
